import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Save, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  TrendingUp,
  Users,
  Globe,
  BarChart3,
  Target,
  Shield,
  CheckCircle,
  X,
  ClipboardCheck,
  TrendingDown,
  Crown,
  FileCheck,
  AlertTriangle,
  Star
} from 'lucide-react'
import { useContent } from '@context/ContentContext'
import { useFirestore } from '@hooks/useFirestore'

const ServicesManager = () => {
  const { content, updateService, loading } = useContent()
  const { initializeDefaultData } = useFirestore('services')
  const [editingService, setEditingService] = useState(null)
  const [formData, setFormData] = useState({})
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [resetting, setResetting] = useState(false)

  const iconMap = {
    appraisals: ClipboardCheck,
    increment: TrendingUp,
    variable: Target,
    esop: TrendingDown,
    cxo: Crown,
    benefits: Shield,
    policies: FileCheck,
    compliance: AlertTriangle,
    rewards: Star,
    // Legacy mappings for backward compatibility
    equity: TrendingUp,
    executive: Users,
    regional: Globe,
    benchmarking: BarChart3
  }

  const handleEdit = (service) => {
    setEditingService(service.id)
    setFormData({
      title: service.title || '',
      subtitle: service.subtitle || '',
      description: service.description || '',
      features: service.features || [],
      benefits: service.benefits || [],
      color: service.color || 'from-blue-500 to-blue-600',
      bgColor: service.bgColor || 'from-blue-50 to-blue-100'
    })
  }

  const handleCancel = () => {
    setEditingService(null)
    setFormData({})
    setMessage('')
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }))
  }

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), '']
    }))
  }

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const handleSave = async () => {
    if (!editingService) return

    setSaving(true)
    setMessage('')

    try {
      await updateService(editingService, formData)
      setMessage('Service updated successfully!')
      setEditingService(null)
      setFormData({})
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      setMessage('Error updating service: ' + error.message)
    } finally {
      setSaving(false)
    }
  }

  const handleResetServices = async () => {
    if (!confirm('Are you sure you want to reset all services to the latest comprehensive defaults? This will overwrite all existing service data.')) {
      return
    }

    setResetting(true)
    setMessage('')

    try {
      await initializeDefaultData()
      setMessage('Services reset to comprehensive defaults successfully!')
      
      // Clear message after 5 seconds
      setTimeout(() => setMessage(''), 5000)
    } catch (error) {
      setMessage('Error resetting services: ' + error.message)
    } finally {
      setResetting(false)
    }
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-navy-200 border-t-navy-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted">Loading services...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-display-3 text-navy-900 mb-2">Services Manager</h1>
          <p className="text-body text-muted">Manage your service offerings and content</p>
        </div>
        <button
          onClick={handleResetServices}
          disabled={resetting}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {resetting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Resetting...
            </>
          ) : (
            <>
              <TrendingUp className="h-4 w-4" />
              Reset to Comprehensive Services
            </>
          )}
        </button>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-xl ${
            message.includes('Error') 
              ? 'bg-red-50 text-red-700 border border-red-200' 
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}
        >
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5" />
            <span>{message}</span>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {content.services.map((service) => {
          const IconComponent = iconMap[service.id] || Target
          const isEditing = editingService === service.id

          return (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card"
            >
              {isEditing ? (
                <div className="p-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-heading-2 text-navy-900">Edit {service.title}</h3>
                    <button
                      onClick={handleCancel}
                      className="p-2 text-neutral-500 hover:text-neutral-700 rounded-lg hover:bg-neutral-100"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-semibold text-navy-900 mb-2">Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className="form-input"
                    />
                  </div>

                  {/* Subtitle */}
                  <div>
                    <label className="block text-sm font-semibold text-navy-900 mb-2">Subtitle</label>
                    <input
                      type="text"
                      value={formData.subtitle}
                      onChange={(e) => handleChange('subtitle', e.target.value)}
                      className="form-input"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-navy-900 mb-2">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      className="form-textarea"
                    />
                  </div>

                  {/* Features */}
                  <div>
                    <label className="block text-sm font-semibold text-navy-900 mb-2">Features</label>
                    <div className="space-y-2">
                      {formData.features?.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => handleArrayChange('features', index, e.target.value)}
                            className="form-input flex-1"
                            placeholder="Feature description"
                          />
                          <button
                            onClick={() => removeArrayItem('features', index)}
                            className="p-2 text-red-500 hover:text-red-700 rounded-lg hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addArrayItem('features')}
                        className="flex items-center space-x-2 text-navy-700 hover:text-navy-900 font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Feature</span>
                      </button>
                    </div>
                  </div>

                  {/* Benefits */}
                  <div>
                    <label className="block text-sm font-semibold text-navy-900 mb-2">Benefits</label>
                    <div className="space-y-2">
                      {formData.benefits?.map((benefit, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={benefit}
                            onChange={(e) => handleArrayChange('benefits', index, e.target.value)}
                            className="form-input flex-1"
                            placeholder="Benefit description"
                          />
                          <button
                            onClick={() => removeArrayItem('benefits', index)}
                            className="p-2 text-red-500 hover:text-red-700 rounded-lg hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addArrayItem('benefits')}
                        className="flex items-center space-x-2 text-navy-700 hover:text-navy-900 font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Benefit</span>
                      </button>
                    </div>
                  </div>

                  {/* Color Scheme */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-navy-900 mb-2">Icon Color</label>
                      <select
                        value={formData.color}
                        onChange={(e) => handleChange('color', e.target.value)}
                        className="form-input"
                      >
                        <option value="from-blue-500 to-blue-600">Blue</option>
                        <option value="from-purple-500 to-purple-600">Purple</option>
                        <option value="from-green-500 to-green-600">Green</option>
                        <option value="from-orange-500 to-orange-600">Orange</option>
                        <option value="from-red-500 to-red-600">Red</option>
                        <option value="from-teal-500 to-teal-600">Teal</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-navy-900 mb-2">Background Color</label>
                      <select
                        value={formData.bgColor}
                        onChange={(e) => handleChange('bgColor', e.target.value)}
                        className="form-input"
                      >
                        <option value="from-blue-50 to-blue-100">Blue</option>
                        <option value="from-purple-50 to-purple-100">Purple</option>
                        <option value="from-green-50 to-green-100">Green</option>
                        <option value="from-orange-50 to-orange-100">Orange</option>
                        <option value="from-red-50 to-red-100">Red</option>
                        <option value="from-teal-50 to-teal-100">Teal</option>
                      </select>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={handleCancel}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="btn-primary"
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${service.color} rounded-xl flex items-center justify-center`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(service)}
                        className="p-2 text-navy-700 hover:text-navy-900 rounded-lg hover:bg-navy-50"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <a
                        href={`/services#${service.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-neutral-500 hover:text-neutral-700 rounded-lg hover:bg-neutral-50"
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                    </div>
                  </div>

                  <h3 className="text-heading-2 text-navy-900 mb-2">{service.title}</h3>
                  <p className="text-body-small text-muted mb-4">{service.subtitle}</p>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-navy-900 mb-1">Features ({service.features?.length || 0})</p>
                      <p className="text-sm text-muted">Key service features and capabilities</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-navy-900 mb-1">Benefits ({service.benefits?.length || 0})</p>
                      <p className="text-sm text-muted">Client benefits and outcomes</p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

export default ServicesManager 